import { api } from './api';

export interface SprintRow {
  id: string;
  name: string;
  goal: string | null;
  state: 'PLANNED' | 'ACTIVE' | 'COMPLETED';
  startDate: string | null;
  endDate: string | null;
  completedAt: string | null;
  capacityPoints: number;
  issueCount: number;
  plannedPoints: number;
  completedPoints: number;
  createdAt: string;
  updatedAt: string;
}

export interface BacklogIssueRow {
  id: string;
  key: string;
  title: string;
  type: 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';
  priority: 'LOWEST' | 'LOW' | 'MEDIUM' | 'HIGH' | 'HIGHEST';
  statusId: string;
  statusName: string;
  statusCategory: 'TODO' | 'IN_PROGRESS' | 'DONE';
  assigneeId: string | null;
  assigneeName: string | null;
  storyPoints: number | null;
  sprintId: string | null;
}

export interface BacklogResponse {
  sprints: Array<SprintRow & { issues: BacklogIssueRow[] }>;
  backlog: BacklogIssueRow[];
  activeSprintId: string | null;
}

export interface BoardCard {
  id: string;
  key: string;
  title: string;
  type: 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';
  priority: 'LOWEST' | 'LOW' | 'MEDIUM' | 'HIGH' | 'HIGHEST';
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  storyPoints: number | null;
  parentIssueId: string | null;
  parentIssueKey: string | null;
  boardOrder: number | null;
}

export interface BoardColumn {
  statusId: string;
  name: string;
  category: 'TODO' | 'IN_PROGRESS' | 'DONE';
  order: number;
  colorHex: string;
  wipLimit: number | null;
  issues: BoardCard[];
}

export interface BoardResponse {
  sprint: SprintRow | null;
  activeSprintId: string | null;
  columns: BoardColumn[];
}

export async function listSprints(orgId: string, projectId: string, state?: SprintRow['state']) {
  const { data } = await api.get<{ items: SprintRow[] }>(
    `/orgs/${orgId}/projects/${projectId}/sprints`,
    {
      params: state ? { state } : undefined,
    },
  );
  return data.items;
}

export async function createSprint(
  orgId: string,
  projectId: string,
  body: {
    name: string;
    goal?: string;
    startDate?: string;
    endDate?: string;
    capacityPoints?: number;
  },
) {
  const { data } = await api.post<SprintRow>(`/orgs/${orgId}/projects/${projectId}/sprints`, body);
  return data;
}

export async function updateSprintApi(
  orgId: string,
  projectId: string,
  sprintId: string,
  body: {
    name?: string;
    goal?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    capacityPoints?: number;
  },
) {
  const { data } = await api.patch<SprintRow>(
    `/orgs/${orgId}/projects/${projectId}/sprints/${sprintId}`,
    body,
  );
  return data;
}

export async function startSprintApi(
  orgId: string,
  projectId: string,
  sprintId: string,
  body: { startDate?: string; endDate?: string } = {},
) {
  const { data } = await api.post<SprintRow>(
    `/orgs/${orgId}/projects/${projectId}/sprints/${sprintId}/start`,
    body,
  );
  return data;
}

export async function completeSprintApi(
  orgId: string,
  projectId: string,
  sprintId: string,
  body: { carryoverTarget: 'BACKLOG' | 'SPRINT'; targetSprintId?: string },
) {
  const { data } = await api.post<{
    sprint: SprintRow;
    reassignedIssues: number;
    report: { plannedPoints: number; completedPoints: number; carryoverPoints: number };
  }>(`/orgs/${orgId}/projects/${projectId}/sprints/${sprintId}/complete`, body);
  return data;
}

export async function getBacklogApi(orgId: string, projectId: string) {
  const { data } = await api.get<BacklogResponse>(`/orgs/${orgId}/projects/${projectId}/backlog`);
  return data;
}

export async function getBoardApi(orgId: string, projectId: string, sprintId?: string) {
  const { data } = await api.get<BoardResponse>(`/orgs/${orgId}/projects/${projectId}/board`, {
    params: sprintId ? { sprintId } : undefined,
  });
  return data;
}

export async function moveBoardIssueApi(
  orgId: string,
  projectId: string,
  body: {
    issueId: string;
    statusId: string;
    beforeIssueId?: string | null;
    afterIssueId?: string | null;
  },
) {
  const { data } = await api.post<{ issueId: string; statusId: string; boardOrder: number }>(
    `/orgs/${orgId}/projects/${projectId}/board/move`,
    body,
  );
  return data;
}

// Assign / unassign an issue to a sprint via the generic issue PATCH endpoint.
export async function moveIssueToSprint(orgId: string, issueKey: string, sprintId: string | null) {
  const { data } = await api.patch(`/orgs/${orgId}/issues/${issueKey}`, { sprintId });
  return data as { sprintId: string | null };
}
